import { useState, useEffect, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAuth } from '../firebase/useAuth';
import { fetchWords, fetchSentences, refetchSingleItem } from '../firebase/course';
import { useMetadata } from '../contexts/MetadataProvider';
import { addMultipleWordsBatch } from '../firebase/words';
import { useForecast } from '../contexts/ForecastProvider';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import SidebarControls from '../components/course/SidebarControls';
import { useToast } from '../context/ToastContext';
import ActionControls from '../components/course/ActionControls';
import MobileSidebar from '../components/course/MobileSidebar';
import { FunnelIcon, ChevronDownIcon, BookOpenIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../components/common/GlassyIcon';
import { useModal } from '../context/ModalContext';
import { levelMapping } from '../utils/mappings';
import DetailsModal from '../components/modals/DetailsModal/DetailsModal';
import Spinner from '../components/common/Spinner';
import ItemGrid from '../components/common/ItemGrid';
import CourseContentGridSkeleton from '../components/course/skeletons/CourseContentGridSkeleton';

const MAX_SELECTION = 20;

function CourseContentPage({ contentType = 'words' }) {
  const { userId, userLoading, subscriptionStatus, emailVerified } = useAuth();
  const { learnedWordIds, learnedSentenceIds, wordIdsLoading, sentenceIdsLoading } = useMetadata();
  const learnedWordIdsRef = useRef(learnedWordIds);
  const learnedSentenceIdsRef = useRef(learnedSentenceIds);
  useEffect(() => { learnedWordIdsRef.current = learnedWordIds; }, [learnedWordIds]);
  useEffect(() => { learnedSentenceIdsRef.current = learnedSentenceIds; }, [learnedSentenceIds]);
  const { showModal, hideModal } = useModal();
  const { sentenceCounts, wordCounts, loading: countsLoading } = useForecast();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const lastVisibleRef = useRef(null);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [learnedFilter, setLearnedFilter] = useState('all');
  const [grammarFilter, setGrammarFilter] = useState(false);
  const [kanjiComplexityMode, setKanjiComplexityMode] = useState('all');
  const [kanjiComplexityValue, setKanjiComplexityValue] = useState('');
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const { showToast } = useToast();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showStickyFilterButton, setShowStickyFilterButton] = useState(false);
  const mobileFiltersRef = useRef(null);
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isInitialLoadingRef = useRef(true);
  const autoLoadingRef = useRef(false);

  // Get total counts from ForecastProvider directly for all users (logged in or not)
  const totalCounts = contentType === 'words' ? wordCounts : sentenceCounts;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyFilterButton(!entry.isIntersecting);
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    const currentRef = mobileFiltersRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Handle item updates from DetailsModal actions
  const handleItemUpdate = useCallback(async (itemId) => {
    try {
      // Refetch the updated item data from server
      const updatedItem = await refetchSingleItem(itemId, userId, contentType);
      
      setItems(prevItems => {
        const itemIndex = prevItems.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return prevItems;
        
        const newItems = [...prevItems];
        // Replace with the updated item data
        newItems[itemIndex] = updatedItem;
        return newItems;
      });
    } catch (error) {
      console.error('Failed to refetch item:', error);
    }
  }, [userId, contentType]);

  useEffect(() => {
    setSelectedLevel('all');
    setLearnedFilter('all');
    setGrammarFilter(false);
    setKanjiComplexityMode('all');
    setKanjiComplexityValue('');
    setIsSelectMode(false);
    setSelectedItemIds(new Set());
    setIsMobileSidebarOpen(false);
    setIsMobileFiltersVisible(false);
    setIsInitialLoading(true);
    isInitialLoadingRef.current = true;
    autoLoadingRef.current = false;
    // Clear items immediately when content type changes
    setItems([]);
    setHasMore(true);
    lastVisibleRef.current = null;
  }, [contentType]);

  const itemsPerPage = contentType === 'words' ? 54 : 36;
  const disableFurtherSelection = isSelectMode && selectedItemIds.size >= MAX_SELECTION;

  const getHeaderConfig = () => {
    if (contentType === 'words') {
      return { icon: BookOpenIcon, title: 'Words' };
    } else {
      return {
        icon: ChatBubbleLeftRightIcon,
        title: 'Sentences',
      };
    }
  };

  const loadItems = useCallback(
    async (isInitialLoad = false) => {
      if (userLoading) return;
      const idsLoading = contentType === 'words' ? wordIdsLoading : sentenceIdsLoading;
      if (idsLoading) return;
      setIsLoading(true);
      const fetchFunction = contentType === 'words' ? fetchWords : fetchSentences;
      try {
        const lastDoc = isInitialLoad ? null : lastVisibleRef.current;
        const currentLearnedIds = contentType === 'words' ? learnedWordIdsRef.current : learnedSentenceIdsRef.current;

        let kanjiFilter = null;
        if (kanjiComplexityMode === 'kana') kanjiFilter = { op: '==', val: 0 };
        else if (kanjiComplexityMode === 'nonwk') kanjiFilter = { op: '==', val: 61 };
        else if (kanjiComplexityMode === 'range' && kanjiComplexityValue) {
          const num = parseInt(kanjiComplexityValue);
          // Valid range → filter by exact level. Outside 1-60 → sentinel -1 returns no results.
          kanjiFilter = { op: '==', val: (num >= 1 && num <= 60) ? num : -1 };
        }

        const result = contentType === 'words'
          ? await fetchFunction(selectedLevel, itemsPerPage, lastDoc, userId, learnedFilter, currentLearnedIds, grammarFilter)
          : await fetchFunction(selectedLevel, itemsPerPage, lastDoc, userId, learnedFilter, currentLearnedIds, kanjiFilter);
        const newItems = contentType === 'words' ? result.words : result.sentences;
        
        
        setItems((prev) => {
          let newItemsList;
          if (isInitialLoad) {
            newItemsList = newItems;
          } else {
            // Filter out duplicates to prevent UI issues while we debug the root cause
            const existingIds = new Set(prev.map(item => item.id));
            const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
            
            
            newItemsList = [...prev, ...uniqueNewItems];
          }
          
          // End initial loading state when we get items OR when there are no more items
          if (isInitialLoadingRef.current) {
            if (newItemsList.length > 0 || !result.hasMore) {
              setIsInitialLoading(false);
              isInitialLoadingRef.current = false;
            } else if (newItems.length === 0 && result.hasMore && !autoLoadingRef.current) {
              // Continue loading immediately if we got 0 filtered items
              autoLoadingRef.current = true;
              // Call directly since we have proper race protection
              loadItems(false).finally(() => {
                autoLoadingRef.current = false;
              });
            }
          }
          
          return newItemsList;
        });
        
        lastVisibleRef.current = result.lastVisible;
        setHasMore(result.hasMore);
      } catch (error) {
        showToast(`Failed to load ${contentType}.`, 'error');
        setIsInitialLoading(false);
        isInitialLoadingRef.current = false;
      } finally {
        setIsLoading(false);
      }
    },
    // showToast is stable from context but eslint can't detect it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentType, learnedFilter, grammarFilter, selectedLevel, kanjiComplexityMode, kanjiComplexityValue, userId, userLoading, itemsPerPage, wordIdsLoading, sentenceIdsLoading]
  );

  useEffect(() => {
    if (userLoading) return;
    setItems([]);
    lastVisibleRef.current = null;
    setHasMore(true);
    setIsInitialLoading(true);
    isInitialLoadingRef.current = true;
    autoLoadingRef.current = false;
    loadItems(true);
  }, [selectedLevel, learnedFilter, grammarFilter, userLoading, userId, contentType, loadItems]);

  // Auto-load more items if there aren't enough to create a scrollable area
  useEffect(() => {
    const checkAndLoadMore = async () => {
      if (isLoading || !hasMore || isInitialLoadingRef.current) return;
      
      // Check if the page is scrollable
      const isScrollable = document.documentElement.scrollHeight > window.innerHeight;
      
      // Only auto-load if page isn't scrollable and we have few items
      if (!isScrollable && items.length < itemsPerPage) {
        await loadItems(false);
      }
    };

    // Small delay to ensure DOM is updated
    const timer = setTimeout(checkAndLoadMore, 100);
    return () => clearTimeout(timer);
  }, [items.length, hasMore, isLoading, itemsPerPage, loadItems, isInitialLoading]);

  const handleSelectItem = useCallback(
    (itemId) => {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        if (next.has(itemId)) {
          next.delete(itemId);
        } else if (prev.size < MAX_SELECTION) {
          next.add(itemId);
        } else {
          showToast(`You can only select up to ${MAX_SELECTION} items.`, 'warning');
        }
        return next;
      });
    },
    [showToast]
  );

  const handleBulkAdd = async () => {
    if (!userId || selectedItemIds.size === 0) return;
    if (!subscriptionStatus.isSubscribed) {
      const selectedWords = items.filter((w) => selectedItemIds.has(w.id));
      if (selectedWords.some((w) => w.level > 0 && w.level < 5)) {
        showToast('Your selection contains premium words. Please upgrade to add them.', 'error');
        return;
      }
    }
    setIsBulkAdding(true);
    try {
      const result = await addMultipleWordsBatch(userId, Array.from(selectedItemIds), subscriptionStatus.isSubscribed);
      if (result.alreadyExisted) {
        showToast(result.errors[0] || 'Some words are already in your list. No words were added.', 'error');
        return;
      }
      const parts = [];
      if (result.addedCount > 0) parts.push(`${result.addedCount} word${result.addedCount > 1 ? 's were' : ' was'} added.`);
      if (result.skippedPremiumCount > 0)
        parts.push(`${result.skippedPremiumCount} premium word${result.skippedPremiumCount > 1 ? 's were' : ' was'} skipped.`);
      if (result.errorCount > 0) parts.push(`${result.errorCount} error${result.errorCount > 1 ? 's' : ''} occurred.`);
      showToast(parts.join(' '), 'success');
    } catch (err) {
      showToast(`An unexpected error occurred: ${err.message}`, 'error');
    } finally {
      setIsBulkAdding(false);
      setIsSelectMode(false);
      setSelectedItemIds(new Set());
    }
  };


  const handleOpenModal = useCallback(
    (item) => {
      showModal(<DetailsModal item={item} handleClose={hideModal} onUpdate={handleItemUpdate} />);
    },
    [showModal, hideModal, handleItemUpdate]
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSelectMode = () => {
    setIsSelectMode((prev) => !prev);
    setSelectedItemIds(new Set());
    setIsMobileSidebarOpen(false);
  };

  const passedProps = {
    level: selectedLevel,
    setLevel: setSelectedLevel,
    filter: learnedFilter,
    setFilter: setLearnedFilter,
    grammarFilter,
    setGrammarFilter,
    kanjiComplexityMode,
    setKanjiComplexityMode,
    kanjiComplexityValue,
    setKanjiComplexityValue,
    isSelectMode,
    isLoggedIn: userId,
    contentType,
    onToggleSelectMode: toggleSelectMode,
    selectedCount: selectedItemIds.size,
    maxSelection: MAX_SELECTION,
    onBulkAdd: handleBulkAdd,
    onCancelSelectMode: toggleSelectMode,
    isBulkAdding,
    bulkAddDisabled: !emailVerified,
    bulkAddTooltip: 'Please verify your email to add words.',
  };

  const headerConfig = getHeaderConfig();
  const HeaderIcon = headerConfig.icon;

  const formatTotalCount = (count) => {
    if (count === undefined || count === null) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toLocaleString();
  };

  const { count: displayCount, label: displayLabel } = (() => {
    if (!totalCounts) return { count: 0, label: 'Total' };
    if (selectedLevel === 'all') {
      const total = Object.values(totalCounts).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
      return { count: total, label: 'Total' };
    }
    const levelNumber = selectedLevel.replace('n', '');
    const countForLevel = totalCounts[levelNumber] || 0;
    const label = levelMapping[levelNumber] || selectedLevel.toUpperCase();
    return { count: countForLevel, label };
  })();

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
        <main className="flex-grow">
          {isSelectMode && showStickyFilterButton && (
            <div className="block lg:hidden sticky top-16 z-30 px-4 py-3 bg-background-secondary border-b border-border overflow-x-hidden">
              <div className="max-w-full">
                <ActionControls {...passedProps} />
              </div>
            </div>
          )}
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="relative mb-8 p-6 rounded-2xl border-2 border-border overflow-hidden animate-fade-in bg-background-secondary/80">
              <div className="relative z-10 flex items-center justify-between text-content-primary">
                <div className="flex items-center gap-4">
                  <GlassyIcon 
                    icon={HeaderIcon} 
                    size="lg" 
                    customGradient={contentType === 'words' 
                      ? 'linear-gradient(135deg, var(--color-words), var(--color-words-accent))'
                      : 'linear-gradient(135deg, var(--color-sentences), var(--color-sentences-accent))'
                    }
                    customShadow={contentType === 'words'
                      ? '0 4px 6px -1px var(--color-words-shadow), 0 2px 4px -1px var(--color-words-shadow)'
                      : '0 4px 6px -1px var(--color-sentences-shadow), 0 2px 4px -1px var(--color-sentences-shadow)'
                    }
                    className="w-12 h-12"
                  />
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-black leading-tight">{headerConfig.title}</h1>
                  </div>
                </div>
                <div className="flex items-center">
                  {countsLoading ? (
                    <div className="w-16 h-12 bg-border rounded-lg animate-pulse"></div>
                  ) : (
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold">{formatTotalCount(displayCount)}</div>
                      <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-content-secondary">{displayLabel}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-6 gap-8">
              <aside className="hidden lg:block lg:col-span-1 xl:col-span-1 lg:sticky top-24 self-start">
                <SidebarControls {...passedProps} />
              </aside>
              <div className="lg:col-span-4 xl:col-span-5">
                <div ref={mobileFiltersRef} className="lg:hidden mb-6">
                  <button
                    onClick={() => setIsMobileFiltersVisible(!isMobileFiltersVisible)}
                    className="flex w-full items-center justify-between rounded-lg bg-background-secondary p-3 shadow-sm border border-border hover:bg-background-secondary/80 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="h-5 w-5 text-content-secondary" />
                      <span className="font-semibold text-content-primary">Filters</span>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-content-secondary transition-transform duration-200 ${isMobileFiltersVisible ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isMobileFiltersVisible && (
                    <div className="mt-4 rounded-lg bg-background-secondary p-4 border border-border animate-fade-in">
                      <SidebarControls {...passedProps} isMobileView />
                    </div>
                  )}
                </div>

                {isInitialLoading ? (
                  <CourseContentGridSkeleton contentType={contentType} />
                ) : (
                  <InfiniteScroll
                    dataLength={items.length}
                    next={() => loadItems(false)}
                    hasMore={hasMore}
                    loader={
                      <div className="my-8">
                        <Spinner />
                      </div>
                    }
                    endMessage={<p className="text-center text-content-secondary my-8">You&apos;ve reached the end!</p>}
                  >
                    <ItemGrid
                      items={items}
                      contentType={contentType}
                      onCardClick={handleOpenModal}
                      isSelectMode={isSelectMode}
                      onSelectWord={handleSelectItem}
                      isSelectedCheck={(id) => selectedItemIds.has(id)}
                      selectionDisabled={disableFurtherSelection}
                      learnedIds={contentType === 'words' ? learnedWordIds : learnedSentenceIds}
                    />
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </div>
        </main>
        <MainFooter />
        <>
          {showStickyFilterButton && !isSelectMode && (
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden fixed top-24 left-4 z-30 flex items-center justify-center h-12 w-12 bg-background-primary rounded-full shadow-lg border border-border hover:shadow-xl transition-all duration-200"
              aria-label="Open Filters"
            >
              <FunnelIcon className="h-6 w-6 text-content-primary" />
            </button>
          )}
          <MobileSidebar isOpen={isMobileSidebarOpen} onClose={() => setIsMobileSidebarOpen(false)}>
            <SidebarControls {...passedProps} />
          </MobileSidebar>
        </>
      </div>
    </SkeletonTheme>
  );
}

export default CourseContentPage;
